import type { ExhibitionLocalization, MuseumContent } from "./contracts";

export type Language = "es" | "en";

export type UiCopy = {
  locale: string;
  languageLabel: string;
  pageDescription: string;
  canvasLabel: string;
  museumEyebrow: string;
  fallbackTitle: string;
  loadingThesis: string;
  preparing: string;
  exhibitionsTitle: string;
  exhibitionsIntro: string;
  openExhibition: string;
  emptyExhibitions: string;
  backToExhibitions: string;
  notFoundTitle: string;
  notFoundMessage: string;
  enter: string;
  routeAndWorks: string;
  howToVisit: string;
  pauseEyebrow: string;
  pauseTitle: string;
  pauseMessage: string;
  continueVisit: string;
  fatalEyebrow: string;
  fatalTitle: string;
  retry: string;
  consultRoute: string;
  previousArtwork: string;
  nextArtwork: string;
  works: string;
  home: string;
  help: string;
  visitControls: string;
  joystickLabel: string;
  viewArtwork: string;
  mapTitle: string;
  mapLabel: string;
  presetLabel: string;
  presetPlaceholder: string;
  presetHint: string;
  closeArtwork: string;
  routeEyebrow: string;
  routeTitle: string;
  closeRoute: string;
  routeIntro: string;
  helpTitle: string;
  closeHelp: string;
  desktopTitle: string;
  desktopHelp: string;
  touchTitle: string;
  touchHelp: string;
  tourTitle: string;
  tourHelp: string;
  author: string;
  century: string;
  styles: string;
  deities: string;
  mortals: string;
  museum: string;
  ready: (rooms: number, artworks: number) => string;
  corridor: string;
  routeCompleted: string;
  loadPhases: Record<"manifest" | "report" | "content" | "scene" | "validated", string>;
  artworkKicker: (order: number, total: number, room: string) => string;
  artworkImageLabel: (title: string, author: string) => string;
  viewFullscreen: (title: string, author: string) => string;
  exitFullscreen: (title: string) => string;
  towards: (title: string) => string;
  towardsPassage: string;
  selection: (title: string) => string;
  visitStarted: (title: string) => string;
  roomChanged: (room: string) => string;
  activeArtwork: (order: number, total: number, title: string) => string;
  completedWith: (title: string) => string;
  navigationPosition: (title: string) => string;
  resetAnnouncement: string;
  pointerLockFailure: string;
  invalidLink: string;
  unknownArtwork: string;
  defaultPause: string;
  webglFailure: string;
  contextLoss: string;
  identityFailure: string;
  missingAsset: string;
  genericFailure: string;
};

const STORAGE_KEY = "virtual-museum-language";

export function readLanguage(storage: Storage = window.localStorage): Language {
  try {
    return storage.getItem(STORAGE_KEY) === "en" ? "en" : "es";
  } catch {
    return "es";
  }
}

export function storeLanguage(language: Language, storage: Storage = window.localStorage): void {
  try {
    storage.setItem(STORAGE_KEY, language);
  } catch {
    // Restricted storage must never prevent a visit.
  }
}

export function copyFor(language: Language): UiCopy {
  return COPY[language];
}

export function localizeContent(
  content: MuseumContent,
  localization: ExhibitionLocalization | null,
): MuseumContent {
  if (localization === null) return content;
  const rooms = new Map(localization.rooms.map((room) => [room.id, room]));
  const artworks = new Map(localization.artworks.map((artwork) => [artwork.id, artwork]));
  return {
    ...content,
    title: localization.title,
    thesis: localization.thesis,
    rooms: content.rooms.map((room) => ({ ...room, title: rooms.get(room.id)?.title ?? room.title })),
    artworks: content.artworks.map((artwork) => ({
      ...artwork,
      ...(artworks.get(artwork.id) ?? {}),
    })),
  };
}

const COPY: Record<Language, UiCopy> = {
  es: {
    locale: "es",
    languageLabel: "Idioma",
    pageDescription: "Exposiciones virtuales creadas a partir de un catálogo de arte.",
    canvasLabel: "Vista tridimensional del museo. El recorrido también está disponible como lista.",
    museumEyebrow: "Museo virtual",
    fallbackTitle: "Museo virtual",
    loadingThesis: "Cargando relato curatorial…",
    preparing: "Preparando los contratos del museo…",
    exhibitionsTitle: "Exposiciones",
    exhibitionsIntro: "Elige una exposición para comenzar la visita.",
    openExhibition: "Abrir exposición",
    emptyExhibitions: "Todavía no hay exposiciones publicadas.",
    backToExhibitions: "Volver a exposiciones",
    notFoundTitle: "Exposición no encontrada",
    notFoundMessage: "La exposición solicitada no existe o no está publicada.",
    enter: "Entrar al museo",
    routeAndWorks: "Recorrido y obras",
    howToVisit: "Cómo visitar",
    pauseEyebrow: "Visita en pausa",
    pauseTitle: "Continúa cuando quieras",
    pauseMessage: "El movimiento está detenido. Tu posición no ha cambiado.",
    continueVisit: "Continuar visita",
    fatalEyebrow: "No se pudo abrir el museo",
    fatalTitle: "Error de carga",
    retry: "Reintentar",
    consultRoute: "Consultar recorrido",
    previousArtwork: "Obra anterior",
    nextArtwork: "Obra siguiente",
    works: "Obras",
    home: "Inicio",
    help: "Ayuda",
    visitControls: "Controles de la visita",
    joystickLabel: "Control táctil de movimiento",
    viewArtwork: "Ver obra",
    mapTitle: "Plano del museo",
    mapLabel: "Plano de las salas, la posición del visitante y las obras",
    presetLabel: "Ir directamente a una obra",
    presetPlaceholder: "Ir a una obra…",
    presetHint: "También puedes usar las teclas numéricas disponibles",
    closeArtwork: "Cerrar información de la obra",
    routeEyebrow: "Recorrido curatorial",
    routeTitle: "Obras de la exposición",
    closeRoute: "Cerrar recorrido",
    routeIntro: "Abre cualquier obra en formato accesible. Tu posición no cambiará.",
    helpTitle: "Controles",
    closeHelp: "Cerrar ayuda",
    desktopTitle: "Escritorio",
    desktopHelp: "WASD o flechas para caminar; ratón para mirar; apunta a una obra y pulsa E.",
    touchTitle: "Pantalla táctil",
    touchHelp: "Usa el control izquierdo para caminar y arrastra sobre la vista para mirar.",
    tourTitle: "Recorrido",
    tourHelp: "La flecha señala la obra activa. Usa Anterior, Siguiente u Obras.",
    author: "Autoría",
    century: "Siglo",
    styles: "Estilos",
    deities: "Deidades",
    mortals: "Mortales",
    museum: "Museo",
    ready: (rooms, artworks) => `Museo preparado: ${rooms} sala(s) y ${artworks} obra(s).`,
    corridor: "Pasaje entre salas",
    routeCompleted: "Recorrido completado",
    loadPhases: {
      manifest: "Validando el manifiesto…",
      report: "Comprobando el informe de Blender…",
      content: "Cargando el recorrido y las reseñas…",
      scene: "Verificando la escena tridimensional…",
      validated: "Preparando cámara, colisiones e interacción…",
    },
    artworkKicker: (order, total, room) => `Obra ${order} de ${total} · ${room}`,
    artworkImageLabel: (title, author) => `${title}, de ${author}`,
    viewFullscreen: (title, author) => `Ver ${title}, de ${author}, a pantalla completa`,
    exitFullscreen: (title) => `Salir de la vista a pantalla completa de ${title}`,
    towards: (title) => `Hacia ${title}`,
    towardsPassage: "Hacia el pasaje",
    selection: (title) => `E · ${title}`,
    visitStarted: (title) => `Visita iniciada. Obra activa: ${title}.`,
    roomChanged: (room) => `Espacio actual: ${room}.`,
    activeArtwork: (order, total, title) => `Obra activa ${order} de ${total}: ${title}.`,
    completedWith: (title) => `Recorrido completado con ${title}.`,
    navigationPosition: (title) => `Posición de visita: ${title}. La obra está frente a ti.`,
    resetAnnouncement: "Has vuelto a la entrada del museo.",
    pointerLockFailure: "El navegador no concedió el control del ratón. Inténtalo de nuevo.",
    invalidLink: "El enlace de obra no era válido y se ha descartado.",
    unknownArtwork: "La obra indicada no pertenece a esta exposición.",
    defaultPause: "El movimiento está detenido. Tu posición no ha cambiado.",
    webglFailure: "Este navegador no ofrece WebGL2. La visita 3D requiere un navegador compatible.",
    contextLoss: "El navegador perdió el contexto gráfico. Recarga para reconstruir la visita.",
    identityFailure: "Los archivos publicados no pertenecen a la misma construcción.",
    missingAsset: "Falta uno de los archivos generados. Publica de nuevo los recursos.",
    genericFailure: "La escena no ha podido validarse completamente.",
  },
  en: {
    locale: "en",
    languageLabel: "Language",
    pageDescription: "Virtual exhibitions built from an art catalogue.",
    canvasLabel: "Three-dimensional museum view. The tour is also available as a list.",
    museumEyebrow: "Virtual museum",
    fallbackTitle: "Virtual museum",
    loadingThesis: "Loading the curatorial narrative…",
    preparing: "Preparing the museum contracts…",
    exhibitionsTitle: "Exhibitions",
    exhibitionsIntro: "Choose an exhibition to begin your visit.",
    openExhibition: "Open exhibition",
    emptyExhibitions: "There are no published exhibitions yet.",
    backToExhibitions: "Back to exhibitions",
    notFoundTitle: "Exhibition not found",
    notFoundMessage: "The requested exhibition does not exist or is not published.",
    enter: "Enter the museum",
    routeAndWorks: "Tour and artworks",
    howToVisit: "How to visit",
    pauseEyebrow: "Visit paused",
    pauseTitle: "Continue whenever you are ready",
    pauseMessage: "Movement is paused. Your position has not changed.",
    continueVisit: "Continue visit",
    fatalEyebrow: "The museum could not be opened",
    fatalTitle: "Loading error",
    retry: "Try again",
    consultRoute: "View the tour",
    previousArtwork: "Previous artwork",
    nextArtwork: "Next artwork",
    works: "Artworks",
    home: "Home",
    help: "Help",
    visitControls: "Visit controls",
    joystickLabel: "Touch movement control",
    viewArtwork: "View artwork",
    mapTitle: "Museum map",
    mapLabel: "Map of the galleries, visitor position, and artworks",
    presetLabel: "Go directly to an artwork",
    presetPlaceholder: "Go to an artwork…",
    presetHint: "You can also use the available number keys",
    closeArtwork: "Close artwork information",
    routeEyebrow: "Curatorial tour",
    routeTitle: "Exhibition artworks",
    closeRoute: "Close tour",
    routeIntro: "Open any artwork in an accessible format. Your position will not change.",
    helpTitle: "Controls",
    closeHelp: "Close help",
    desktopTitle: "Desktop",
    desktopHelp: "Use WASD or arrows to walk, the mouse to look, and E to inspect an artwork.",
    touchTitle: "Touchscreen",
    touchHelp: "Use the left control to walk and drag across the view to look around.",
    tourTitle: "Tour",
    tourHelp: "The arrow points to the active artwork. Use Previous, Next, or Artworks.",
    author: "Artist",
    century: "Century",
    styles: "Styles",
    deities: "Deities",
    mortals: "Mortals",
    museum: "Museum",
    ready: (rooms, artworks) => `Museum ready: ${rooms} room(s) and ${artworks} artwork(s).`,
    corridor: "Passage between galleries",
    routeCompleted: "Tour completed",
    loadPhases: {
      manifest: "Validating the manifest…",
      report: "Checking the Blender report…",
      content: "Loading the tour and essays…",
      scene: "Verifying the three-dimensional scene…",
      validated: "Preparing camera, collisions, and interaction…",
    },
    artworkKicker: (order, total, room) => `Artwork ${order} of ${total} · ${room}`,
    artworkImageLabel: (title, author) => `${title}, by ${author}`,
    viewFullscreen: (title, author) => `View ${title}, by ${author}, full screen`,
    exitFullscreen: (title) => `Exit the full-screen view of ${title}`,
    towards: (title) => `Towards ${title}`,
    towardsPassage: "Towards the passage",
    selection: (title) => `E · ${title}`,
    visitStarted: (title) => `Visit started. Active artwork: ${title}.`,
    roomChanged: (room) => `Current space: ${room}.`,
    activeArtwork: (order, total, title) => `Active artwork ${order} of ${total}: ${title}.`,
    completedWith: (title) => `Tour completed with ${title}.`,
    navigationPosition: (title) => `Viewing position: ${title}. The artwork is in front of you.`,
    resetAnnouncement: "You have returned to the museum entrance.",
    pointerLockFailure: "The browser did not grant mouse control. Try again.",
    invalidLink: "The artwork link was invalid and has been discarded.",
    unknownArtwork: "The requested artwork does not belong to this exhibition.",
    defaultPause: "Movement is paused. Your position has not changed.",
    webglFailure: "This browser does not support WebGL2. The 3D visit requires a compatible browser.",
    contextLoss: "The browser lost its graphics context. Reload to reconstruct the visit.",
    identityFailure: "The published files do not belong to the same build.",
    missingAsset: "A generated file is missing. Publish the assets again.",
    genericFailure: "The scene could not be fully validated.",
  },
};
